import { HeadersFunction, LoaderFunctionArgs, MetaArgs, redirect } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import {
	GQLProductStatusType,
	GQLProductTypeType,
	GetBrandsDocument,
	GetCategoriesDocument,
	GetCategoryDocument,
	GetColourGroupsDocument,
	GetOperationalSettingsDocument,
	GetProductCollectionsDocument,
	GetProductListingPageDocument,
	GetProductTilesWithMinMaxDocument,
	GetSizesDocument,
	GetWebsiteRedirectsDocument,
} from '@theprintbar/graphql';
import { keystoneSudoContext, runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonHour } from '@theprintbar/server';
import { WebRoutes, invariantError, invariantNotFound } from '@theprintbar/shared';
import { ProductListingPage, getProductSearchVariables } from '@theprintbar/ui';
import { SitemapFunction } from 'remix-sitemap';
import { serverOnly$ } from 'vite-env-only/macros';
import { useRootData } from '~/root';
import { contentMeta } from '~/utils/contentMeta';
import { listingHandle, withListingRobots } from '~/utils/listingSeo';
import { getListingStatus, getRelatedListingProducts } from '~/utils/listingStatus.server';

export const handle = listingHandle;

export const meta = (args: MetaArgs<typeof loader>) =>
	withListingRobots(contentMeta({ data: args.data?.category, matches: args.matches }), {
		search: args.location.search,
		noindex: !!args.data?.notFound,
	});

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const sitemap: SitemapFunction = serverOnly$(async ({}) => {
	// Empty categories 404, so keep them out of the sitemap
	const categories = await keystoneSudoContext.prisma.category.findMany({
		where: { products: { some: { status: 'published', availableWeb: true } } },
		select: { slug: true, updatedAt: true },
	});

	return (
		categories?.map(({ slug, updatedAt }) => {
			return {
				loc: WebRoutes.Collection(slug),
				lastmod: new Date(updatedAt).toISOString(),
				priority: 0.5,
			};
		}) || []
	);
})!;

export async function loader({ request, params: urlParams }: LoaderFunctionArgs) {
	let slug = urlParams['*'];
	invariantNotFound(slug, 'No product slug provided');
	const { category } = await runSudoGraphql(GetCategoryDocument, { where: { slug } });

	if (!category) {
		const { websiteRedirects } = await runSudoGraphql(GetWebsiteRedirectsDocument, {
			where: { from: { equals: `/collections/${slug}` } },
		});

		if (websiteRedirects?.[0]?.to) {
			return redirect(`${websiteRedirects[0].to}${new URL(request.url).search}`, 301);
		}
		invariantNotFound(category, 'No category found');
	}

	const { params, variables, invalid } = getProductSearchVariables({ request, isWeb: true, category });
	const [
		{ productListingPage },
		{ products, maxPrice, minPrice, totalProducts },
		{ operationalSettings },
		{ brands },
		{ categories },
		{ productCollections },
		{ colourGroups },
		{ sizes },
	] = await Promise.all([
		runSudoGraphql(GetProductListingPageDocument),
		runSudoGraphql(GetProductTilesWithMinMaxDocument, variables),
		runSudoGraphql(GetOperationalSettingsDocument),
		runSudoGraphql(GetBrandsDocument, {
			where: {
				products: {
					some: {
						status: { equals: GQLProductStatusType.Published },
						availableWeb: { equals: true }, // Only include brands with products available on the web
					},
				},
			},
		}),
		runSudoGraphql(GetCategoriesDocument),
		runSudoGraphql(GetProductCollectionsDocument, { where: {} }),
		runSudoGraphql(GetColourGroupsDocument),
		runSudoGraphql(GetSizesDocument),
	]);
	const productTypes = Object.values(GQLProductTypeType);

	invariantError(productListingPage, 'No product listing page found');
	invariantError(operationalSettings, 'No operational settings found');

	const { notFound, empty } = getListingStatus({ params, invalid, totalProducts, take: variables.take });
	const relatedProducts = empty ? await getRelatedListingProducts(category) : null;

	// 404s are returned, not thrown, so users still get the listing and can clear their filters
	return cachedJsonHour(
		{
			category,
			productListingPage,
			initialProducts: products,
			relatedProducts,
			notFound,
			minPrice,
			maxPrice,
			totalProducts,
			brands,
			categories,
			collections: productCollections,
			colours: colourGroups,
			sizes,
			operationalSettings,
			params,
			productTypes,
		},
		{ status: notFound ? 404 : 200 },
	);
}

export default function () {
	const data = useLoaderData<typeof loader>();
	const { globalContent } = useRootData();
	const samedayCartWarning = globalContent?.samedayCartWarning;

	return (
		<ProductListingPage {...data} productSearchURL={WebRoutes.API.commerce.products.search} samedayCartWarning={samedayCartWarning} />
	);
}
